import { useEffect, useState } from "react";
import { toast } from "sonner";
import { rpc } from "../../../lib/rpc";
import { useFlowOperationId } from "../../../hooks/useFlowOperationId";
import { useScanPrefill } from "../../../hooks/useScanPrefill";
import { FlowShell } from "../../../components/mobile/FlowShell";
import { FlowPhotoSlot } from "../../../components/mobile/FlowPhotoSlot";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";

export default function UnknownItemFlow() {
    const flow = useFlowOperationId();
    const prefill = useScanPrefill();
    const [step, setStep] = useState(1);
    const [photoOverview, setPhotoOverview] = useState(null);
    const [photoIdentity, setPhotoIdentity] = useState(null);
    const [photoLocation, setPhotoLocation] = useState(null);
    const [name, setName] = useState("");
    const [compartmentCode, setCompartmentCode] = useState("");
    const [comment, setComment] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [scannedCode, setScannedCode] = useState("");

    useEffect(() => {
        if (!prefill?.unknownCode) return;
        setScannedCode(prefill.unknownCode);
        setComment((current) => current || `Gescannter Code: ${prefill.unknownCode}`);
    }, [prefill]);

    const submit = async () => {
        if (!photoOverview || !name.trim()) {
            return toast.error("Bezeichnung und mindestens ein Foto sind Pflicht.");
        }
        setSubmitting(true);
        // #55: Fotos wandern mit in den Request; der Server verknüpft sie
        // mit dem Fall. Vorher lief die Verknüpfung als nachträglicher
        // PATCH, dessen Fehler still verschluckt wurden.
        const res = await rpc.post("/unknown-items", {
            client_operation_id: flow.clientOperationId,
            proposed_name: name.trim(),
            photo_ids: [photoOverview, photoIdentity, photoLocation].filter(Boolean),
            found_at_compartment_code: compartmentCode.trim() || undefined,
            comment: comment.trim() || undefined,
        });
        setSubmitting(false);
        if (!res.ok) return toast.error(res.error);

        toast.success("Gegenstand aufgenommen. Ein Admin prüft die Anlage.");
        flow.rotate();
        setStep(1);
        setPhotoOverview(null);
        setPhotoIdentity(null);
        setPhotoLocation(null);
        setName("");
        setCompartmentCode("");
        setComment("");
        setScannedCode("");
    };

    const canAdvance =
        (step === 1 && photoOverview) ||
        (step === 2 && name.trim()) ||
        step === 3;

    return (
        <FlowShell
            testId="flow-unknown-item"
            title="Gegenstand aufnehmen"
            kind="Schritt für Schritt"
            stepIndicator={step + " / 3"}
            onSubmit={step === 3 ? submit : () => setStep(step + 1)}
            submitLabel={step === 3 ? "Zur Prüfung senden" : "Weiter"}
            submitDisabled={!canAdvance || submitting}
            submitting={submitting}
        >
            {scannedCode ? (
                <div className="rounded-md border border-border bg-card/60 p-3" data-testid="unk-scanned-code">
                    <div className="text-xs font-medium text-foreground">Unbekannter Scan-Code</div>
                    <code className="mt-1 block break-all text-xs text-muted-foreground">{scannedCode}</code>
                </div>
            ) : null}
            {step === 1 ? (
                <FlowPhotoSlot
                    sessionKey={flow.clientOperationId}
                    label="1 / 3 – Foto des Gegenstands"
                    entityType="unknown_item_case"
                    purpose="unknown_item"
                    required={true}
                    requireSync={true}
                    value={photoOverview}
                    onChange={setPhotoOverview}
                    testId="unk-photo-overview"
                />
            ) : null}

            {step === 2 ? (
                <>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                        2 / 3 – Bezeichnung
                    </Label>
                    <Input
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                        placeholder="z. B. Fahrzeugschlüssel Sprinter"
                        className="h-12"
                        aria-label="Bezeichnung"
                    data-testid="unk-name"
                    />
                    <FlowPhotoSlot
                        sessionKey={flow.clientOperationId}
                        label="Kennzeichnung oder Typenschild (optional)"
                        entityType="unknown_item_case"
                        purpose="serial_number"
                        required={false}
                        requireSync={true}
                        value={photoIdentity}
                        onChange={setPhotoIdentity}
                        testId="unk-photo-identity"
                    />
                </>
            ) : null}

            {step === 3 ? (
                <>
                    <FlowPhotoSlot
                        sessionKey={flow.clientOperationId}
                        label="Fundort oder Fach (optional)"
                        entityType="unknown_item_case"
                        purpose="location_orientation"
                        required={false}
                        requireSync={true}
                        value={photoLocation}
                        onChange={setPhotoLocation}
                        testId="unk-photo-location"
                    />
                    <Input
                        value={compartmentCode}
                        onChange={(event) => setCompartmentCode(event.target.value)}
                        placeholder="Fach-Code, z. B. R1-A-01"
                        className="h-12"
                        aria-label="Fach-Code"
                    data-testid="unk-comp-code"
                    />
                    <Input
                        value={comment}
                        onChange={(event) => setComment(event.target.value)}
                        placeholder="Kommentar (optional)"
                        className="h-12"
                        aria-label="Kommentar"
                    data-testid="unk-comment"
                    />
                </>
            ) : null}
        </FlowShell>
    );
}
